// Dashboard service for KATA Dashboard

import { apiClient, handleApiError } from './api';

export interface DashboardStats {
  overview: {
    total_workshops: number;
    completed_workshops: number;
    ongoing_workshops: number;
    passive_workshops: number;
    planned_workshops: number;
    completion_rate: number;
    status_counts: {
      new: number;
      in_progress: number;
      on_hold: number;
      risk_of_delay: number;
      rejected: number;
      closed: number;
      fully_implemented: number;
    };
  };
  financial: {
    total_estimated_savings: number;
    total_released_savings: number;
    realization_rate: number;
  };
  timeline: {
    overdue_workshops: number;
    upcoming_deadlines: number;
  };
  recent_activity: {
    new_workshops_30d: number;
    completions_30d: number;
  };
  departments: Array<{
    id: number;
    name: string;
    code: string;
    workshop_count: number;
    completed: number;
    late: number;
    estimated_savings: number;
    released_savings: number;
  }>;
  user_context: {
    is_admin: boolean;
    department: string | null;
    can_create: boolean;
    can_edit_all: boolean;
  };
}

export interface WorkshopStatusCounts {
  active: number;        // ongoing workshops
  late: number;          // overdue workshops
  completed: number;     // completed workshops
  total: number;         // total workshops
}

export interface SavingsMetrics {
  estimated_savings: number;      // Total estimated savings
  released_savings: number;       // Total released savings
  realization_rate: number;       // Percentage of realized savings
  formatted_estimated: string;    // Formatted estimated (e.g., "2,703K€")
  formatted_released: string;     // Formatted released (e.g., "1,939K€")
}

export interface DepartmentStats {
  id: number;
  name: string;
  code: string;
  workshop_count: number;
  completed: number;
  late: number;
  completion_rate: number;
  estimated_savings: number;
  released_savings: number;
  color: string; // For UI styling
}

export class DashboardService {
  /**
   * Get comprehensive dashboard statistics
   */
  static async getDashboardStats(filters?: { deadline_from?: string; deadline_to?: string; alv_id?: string }): Promise<DashboardStats> {
    try {
      const params = new URLSearchParams();
      if (filters?.deadline_from) params.append('deadline_from', filters.deadline_from);
      if (filters?.deadline_to) params.append('deadline_to', filters.deadline_to);
      if (filters?.alv_id) params.append('alv_id', filters.alv_id);

      const response = await apiClient.get<DashboardStats>(`/dashboard/stats/?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get workshop status counts (Active, Late, Completed)
   */
  static async getWorkshopStatusCounts(filters?: { deadline_from?: string; deadline_to?: string; alv_id?: string }): Promise<WorkshopStatusCounts> {
    try {
      const stats = await this.getDashboardStats(filters);
      
      return {
        active: stats.overview.ongoing_workshops,
        late: stats.timeline.overdue_workshops,
        completed: stats.overview.completed_workshops,
        total: stats.overview.total_workshops
      };
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Calculate savings realization metrics
   */
  static async getSavingsMetrics(filters?: { deadline_from?: string; deadline_to?: string; alv_id?: string }): Promise<SavingsMetrics> {
    try {
      const stats = await this.getDashboardStats(filters);
      
      const estimated = stats.financial.total_estimated_savings;
      const released = stats.financial.total_released_savings;
      const realization = stats.financial.realization_rate;

      return {
        estimated_savings: estimated,
        released_savings: released,
        realization_rate: realization,
        formatted_estimated: this.formatCurrency(estimated),
        formatted_released: this.formatCurrency(released)
      };
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get department statistics with completion rates
   */
  static async getDepartmentStats(filters?: { deadline_from?: string; deadline_to?: string; alv_id?: string }): Promise<DepartmentStats[]> {
    try {
      const stats = await this.getDashboardStats(filters);
      
      // Define colors for each department
      const departmentColors = [
        '#10B981', // Green
        '#3B82F6', // Blue
        '#F59E0B', // Orange
        '#8B5CF6', // Purple
        '#EF4444', // Red
        '#06B6D4', // Cyan
        '#F97316', // Orange-600
        '#EC4899', // Pink
        '#84CC16', // Lime
        '#6366F1', // Indigo
        '#14B8A6', // Teal
        '#F43F5E'  // Rose
      ];

      return stats.departments.map((dept, index) => ({
        id: dept.id, // Use actual department ID from backend
        name: dept.name,
        code: dept.code,
        workshop_count: dept.workshop_count,
        completed: dept.completed,
        late: dept.late || 0,
        completion_rate: dept.workshop_count > 0 
          ? Math.round((dept.completed / dept.workshop_count) * 100) 
          : 0,
        estimated_savings: dept.estimated_savings,
        released_savings: dept.released_savings,
        color: departmentColors[index % departmentColors.length]
      }));
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get overdue workshops
   */
  static async getOverdueWorkshops(): Promise<any[]> {
    try {
      const response = await apiClient.get('/workshops/overdue/');
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get workshops with upcoming deadlines
   */
  static async getUpcomingDeadlines(): Promise<any[]> {
    try {
      const response = await apiClient.get('/workshops/upcoming_deadlines/');
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Format currency values for display
   */
  static formatCurrency(value: number): string {
    // Backend now returns values in EUR (base currency)
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M€`;
    } else if (value >= 1000) {
      // Show one decimal place for K€ to be more precise
      return `${(value / 1000).toFixed(1)}K€`;
    } else {
      return `${Math.round(value)}€`;
    }
  }
  
  static formatCurrencyWithUnit(value: number | string, unit: 'EUR' | 'KEUR' | 'MEUR'): string {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return 'N/A';
    
    const unitDisplay = {
      'EUR': '€',
      'KEUR': 'K€', 
      'MEUR': 'M€'
    };
    
    return `${numValue.toFixed(1)}${unitDisplay[unit]}`;
  }

  /**
   * Format percentage values
   */
  static formatPercentage(value: number): string {
    return `${Math.round(value * 10) / 10}%`;
  }

  /**
   * Get trend indicator (for month-over-month comparisons)
   */
  static getTrendIndicator(current: number, previous: number): {
    value: number;
    isPositive: boolean;
    formatted: string;
  } {
    if (previous === 0) {
      return {
        value: 0,
        isPositive: true,
        formatted: 'N/A'
      };
    }

    const change = ((current - previous) / previous) * 100;
    const isPositive = change >= 0;
    
    return {
      value: Math.abs(change),
      isPositive,
      formatted: `${isPositive ? '+' : '-'}${Math.round(Math.abs(change) * 10) / 10}%`
    };
  }

  /**
   * Calculate completion rate for a department
   */
  static calculateCompletionRate(completed: number, total: number): number {
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  }

  /**
   * Get color based on completion rate
   */
  static getCompletionRateColor(rate: number): string {
    if (rate >= 90) return '#10B981'; // Green
    if (rate >= 75) return '#F59E0B'; // Orange
    if (rate >= 50) return '#3B82F6'; // Blue
    return '#EF4444'; // Red
  }

  /**
   * Get status color for workshops
   */
  static getStatusColor(status: 'active' | 'late' | 'completed'): string {
    switch (status) {
      case 'completed':
        return '#10B981'; // Green
      case 'active':
        return '#3B82F6'; // Blue
      case 'late':
        return '#EF4444'; // Red
      default:
        return '#6B7280'; // Gray
    }
  }

  /**
   * Calculate savings realization rate
   */
  static calculateRealizationRate(estimated: number, released: number): number {
    if (estimated === 0) return 0;
    return (released / estimated) * 100;
  }

  /**
   * Get recent activity summary
   */
  static async getRecentActivitySummary(): Promise<{
    new_workshops: number;
    completed_workshops: number;
    updated_savings: number;
    overdue_alerts: number;
  }> {
    try {
      const stats = await this.getDashboardStats();
      
      return {
        new_workshops: stats.recent_activity.new_workshops_30d,
        completed_workshops: stats.recent_activity.completions_30d,
        updated_savings: Math.round(stats.financial.total_released_savings / 1000), // In K€
        overdue_alerts: stats.timeline.overdue_workshops
      };
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get workshop distribution summary
   */
  static async getWorkshopDistribution(): Promise<{
    by_status: { [key: string]: number };
    by_department: { [key: string]: number };
    total: number;
  }> {
    try {
      const stats = await this.getDashboardStats();
      
      const by_department: { [key: string]: number } = {};
      stats.departments.forEach(dept => {
        by_department[dept.name] = dept.workshop_count;
      });

      return {
        by_status: {
          'Completed': stats.overview.completed_workshops,
          'Ongoing': stats.overview.ongoing_workshops,
          'Planned': stats.overview.planned_workshops
        },
        by_department,
        total: stats.overview.total_workshops
      };
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }
}
